import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
    Eye,
    Trash2,
    RefreshCw,
    AlertCircle,
    Tag,
    Calendar,
    IndianRupee,
    Percent,
    Search,
    ChevronLeft,
    X
} from 'lucide-react';

const AllDiscounts = () => {
    const [discounts, setDiscounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [detailsModal, setDetailsModal] = useState({ show: false, data: null });
    const navigate = useNavigate();

    useEffect(() => {
        fetchDiscounts();
    }, []);

    const fetchDiscounts = async () => {
        try {
            setLoading(true);
            const { data } = await axios.get('/api/v1/admin/discounts/all', {
                withCredentials: true
            });
            if (data.success) {
                setDiscounts(data.discounts);
            }
        } catch (error) {
            toast.error('Error fetching discounts');
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveDiscount = async (productId) => {
        if (!window.confirm('Are you sure you want to remove this discount?')) {
            return;
        }
        try {
            setLoading(true);
            const { data } = await axios.delete(`/api/v1/admin/discount/${productId}`, {
                withCredentials: true
            });
            if (data.success) {
                toast.success('Discount removed successfully');
                fetchDiscounts();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error removing discount');
        } finally {
            setLoading(false);
        }
    };

    const viewDiscountDetails = async (productId) => {
        try {
            const { data } = await axios.get(`/api/v1/admin/discount/${productId}`, {
                withCredentials: true
            });
            if (data.success) {
                setDetailsModal({ show: true, data: data.discount });
            }
        } catch (error) {
            toast.error('Error fetching discount details');
        }
    };

    const filteredDiscounts = discounts.filter(discount =>
        discount.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        discount.category.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <RefreshCw className="h-8 w-8 text-[#dc2626] animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header with title and back button moved to top right */}
                <div className="flex justify-between items-center mb-4 sm:mb-6">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900 font-['Crimson_Text']">
                        Active Discounts
                    </h1>
                    <button
                        onClick={() => navigate('/admin/dashboard')}
                        className="flex items-center gap-1 sm:gap-2 text-gray-600 hover:text-[#dc2626] transition-colors py-1.5 sm:py-2 px-2.5 sm:px-3 rounded-lg hover:bg-gray-100"
                    >
                        <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="text-xs sm:text-sm hidden xs:inline">Back to Dashboard</span>
                    </button>
                </div>
                {/* Search bar - Made responsive */}
                <div className="mb-4 sm:mb-6">
                    <div className="relative w-full sm:w-64">
                        <input
                            type="text"
                            placeholder="Search discounts..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626]"
                        />
                        <Search className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3 sm:w-4 sm:h-4" />
                    </div>
                </div>
                {discounts.length === 0 ? (
                    <div className="text-center py-8 sm:py-12">
                        <Tag className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-3 sm:mb-4" />
                        <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-1 sm:mb-2">No Active Discounts</h3>
                        <p className="text-xs sm:text-sm text-gray-500">There are currently no active discounts.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-3 sm:gap-6">
                        {filteredDiscounts.map(discount => (
                            <div
                                key={discount.productId}
                                className="bg-white rounded-lg shadow-sm p-3 sm:p-6 hover:shadow-md transition-shadow"
                            >
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                                    {/* Product info - Stacked on mobile */}
                                    <div className="flex items-center space-x-3 sm:space-x-4 mb-3 sm:mb-0">
                                        <img
                                            src={discount.productImage}
                                            alt={discount.productName}
                                            className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded-lg"
                                        />
                                        <div>
                                            <h3 className="text-sm sm:text-base font-medium text-gray-900">{discount.productName}</h3>
                                            <p className="text-xs sm:text-sm text-gray-500">{discount.category}</p>
                                        </div>
                                    </div>
                                    {/* Discount info and actions - Row on mobile */}
                                    <div className="flex items-center justify-between sm:space-x-4">
                                        <div className="text-xs sm:text-sm text-left sm:text-right">
                                            <p className="font-medium text-gray-900">
                                                {discount.discountType === 'percentage' ? (
                                                    <span className="flex items-center">
                                                        <Percent className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                                                        {discount.discountValue}% OFF
                                                    </span>
                                                ) : (
                                                    <span className="flex items-center">
                                                        <IndianRupee className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                                                        {discount.discountValue} OFF
                                                    </span>
                                                )}
                                            </p>
                                            <p className="text-gray-500 text-xs">
                                                Until {new Date(discount.endDate).toLocaleDateString()}
                                            </p>
                                        </div>
                                        <div className="flex space-x-1 sm:space-x-2">
                                            <button
                                                onClick={() => viewDiscountDetails(discount.productId)}
                                                className="p-1.5 sm:p-2 text-gray-600 hover:text-[#dc2626] hover:bg-red-50 rounded-lg transition-colors"
                                                title="View Details"
                                            >
                                                <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                                            </button>
                                            <button
                                                onClick={() => handleRemoveDiscount(discount.productId)}
                                                className="p-1.5 sm:p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                title="Remove Discount"
                                            >
                                                <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {/* Discount Details Modal - Optimized for mobile */}
                {detailsModal.show && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50">
                        <div className="bg-white rounded-xl w-full max-w-md sm:max-w-2xl p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
                            <div className="flex justify-between items-center mb-3 sm:mb-4">
                                <h2 className="text-lg sm:text-xl font-bold text-gray-900">Discount Details</h2>
                                <button
                                    onClick={() => setDetailsModal({ show: false, data: null })}
                                    className="text-gray-500 hover:text-gray-700"
                                >
                                    <X className="w-5 h-5 sm:w-6 sm:h-6" />
                                </button>
                            </div>
                            <div className="space-y-3 sm:space-y-4">
                                <div className="flex items-center space-x-3 sm:space-x-4">
                                    <img
                                        src={detailsModal.data?.productImage}
                                        alt={detailsModal.data?.productName}
                                        className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-lg"
                                    />
                                    <div>
                                        <h3 className="text-base sm:text-lg font-medium">{detailsModal.data?.productName}</h3>
                                        <p className="text-xs sm:text-sm text-gray-500">{detailsModal.data?.category}</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
                                    <div>
                                        <p className="text-gray-500">Discount Type</p>
                                        <p className="font-medium">{detailsModal.data?.discountType}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-500">Discount Value</p>
                                        <p className="font-medium">
                                            {detailsModal.data?.discountType === 'percentage'
                                                ? `${detailsModal.data?.discountValue}%`
                                                : `₹${detailsModal.data?.discountValue}`}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-gray-500">Start Date</p>
                                        <p className="font-medium">
                                            {new Date(detailsModal.data?.startDate).toLocaleDateString()}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-gray-500">End Date</p>
                                        <p className="font-medium">
                                            {new Date(detailsModal.data?.endDate).toLocaleDateString()}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-gray-500">Original Price</p>
                                        <p className="font-medium">₹{detailsModal.data?.originalPrice}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-500">Discounted Price</p>
                                        <p className="font-medium text-[#dc2626]">₹{detailsModal.data?.discountedPrice}</p>
                                    </div>
                                </div>
                                {/* Display per dozen price for egg products */}
                                {detailsModal.data?.priceType === 'perDozen' && (
                                    <div className="mt-3 sm:mt-4 bg-blue-50 p-3 rounded-lg">
                                        <p className="text-xs sm:text-sm text-blue-700 font-medium mb-1">Per Dozen Pricing</p>
                                        <p className="text-xs sm:text-sm text-blue-600">
                                            Original: ₹{detailsModal.data?.originalPrice} per dozen
                                        </p>
                                        <p className="text-xs sm:text-sm text-blue-600">
                                            Discounted: ₹{detailsModal.data?.discountedPrice} per dozen
                                        </p>
                                    </div>
                                )}
                                {/* Display weight options for fixed weight products */}
                                {detailsModal.data?.priceType === 'fixed' && detailsModal.data?.weightOptions && (
                                    <div className="mt-3 sm:mt-4">
                                        <p className="text-xs sm:text-sm text-gray-500 mb-2">Weight Options</p>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4">
                                            {detailsModal.data?.weightOptions.map(option => (
                                                <div key={option.weight} className="bg-gray-50 p-2 sm:p-3 rounded-lg text-xs sm:text-sm">
                                                    <p className="font-medium">{option.weight}</p>
                                                    <p className="text-gray-500">
                                                        Original: ₹{option.price}
                                                    </p>
                                                    <p className="text-[#dc2626]">
                                                        Discounted: ₹{
                                                            detailsModal.data.discountType === 'percentage'
                                                                ? (option.price - (option.price * detailsModal.data.discountValue / 100)).toFixed(2)
                                                                : Math.max(0, option.price - detailsModal.data.discountValue).toFixed(2)
                                                        }
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <div className="mt-4 sm:mt-6 flex justify-end space-x-2 sm:space-x-3">
                                    <button
                                        onClick={() => handleRemoveDiscount(detailsModal.data?.productId)}
                                        className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm bg-red-100 text-[#dc2626] rounded-lg hover:bg-red-200 transition-colors"
                                    >
                                        Remove Discount
                                    </button>
                                    <button
                                        onClick={() => setDetailsModal({ show: false, data: null })}
                                        className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AllDiscounts;